import React, { useEffect, useMemo, useState } from "react";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { ActionModal } from "./ActionModal";

function getActionTone(name = "") {
	if (name.includes("Setor")) return "border-fuchsia-200 bg-fuchsia-50 text-fuchsia-700 dark:border-fuchsia-500/30 dark:bg-fuchsia-500/10 dark:text-fuchsia-200";
	if (name.includes("Etiqueta")) return "border-orange-200 bg-orange-50 text-orange-700 dark:border-orange-500/30 dark:bg-orange-500/10 dark:text-orange-200";
	if (name.includes("Atendente")) return "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-200";
	if (name.includes("Conexão")) return "border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-500/30 dark:bg-sky-500/10 dark:text-sky-200";
	if (name.includes("Status")) return "border-cyan-200 bg-cyan-50 text-cyan-700 dark:border-cyan-500/30 dark:bg-cyan-500/10 dark:text-cyan-200";
	return "border-default-200 bg-default-100 text-default-700 dark:border-default-700 dark:bg-default-800 dark:text-default-200";
}

function resolveActionLabel(action, lookups) {
	const { users, queues, tags, connections } = lookups;
	let targetLabel = "";

	if (action.name.includes("Setor")) {
		targetLabel = queues.find(queue => queue.id === Number(action.should))?.name || "";
	} else if (action.name.includes("Etiqueta")) {
		targetLabel = tags.find(tag => tag.id === Number(action.should))?.name || "";
	} else if (action.name.includes("Atendente")) {
		targetLabel = users.find(user => user.id === Number(action.should))?.name || "";
	} else if (action.name.includes("Conexão")) {
		targetLabel =
			connections.find(connection => connection.id === Number(action.should))?.name || "";
	} else if (action.name.includes("Status")) {
		targetLabel = action.should || "";
	}

	return targetLabel ? `${action.name} -> ${targetLabel}` : action.name;
}

export default function ActionChips({
	optionActions,
	selectedChatbot,
	setSelectedChatbot,
	nodeId,
	optionId,
}) {
	const [users, setUsers] = useState([]);
	const [queues, setQueues] = useState([]);
	const [tags, setTags] = useState([]);
	const [connections, setConnections] = useState([]);
	const [actionModalOpen, setActionModalOpen] = useState(false);

	useEffect(() => {
		let cancelled = false;

		(async () => {
			try {
				const [{ data: usersData }, { data: queuesData }, { data: tagsData }, { data: connectionsData }] =
					await Promise.all([
						api.get("/users"),
						api.get("/queue"),
						api.get("/tags/list"),
						api.get("/whatsapp"),
					]);

				if (cancelled) return;

				setUsers(Array.isArray(usersData?.users) ? usersData.users : []);
				setQueues(Array.isArray(queuesData) ? queuesData : []);
				setTags(Array.isArray(tagsData) ? tagsData : []);
				setConnections(Array.isArray(connectionsData) ? connectionsData : []);
			} catch (err) {
				if (!cancelled) {
					toastError(err);
				}
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [optionActions]);

	const lookups = useMemo(
		() => ({ users, queues, tags, connections }),
		[connections, queues, tags, users]
	);

	const handleDelete = async chipToDelete => {
		try {
			await api.delete(`/chatbot/action/${chipToDelete.id}`);

			setSelectedChatbot(prevState => ({
				...prevState,
				nodes: (prevState.nodes || []).map(node => {
					if (node.id !== nodeId) {
						return node;
					}

					return {
						...node,
						options: (node.options || []).map(option => {
							if (option.id !== optionId) {
								return option;
							}

							return {
								...option,
								action: (option.action || []).filter(action => action.id !== chipToDelete.id),
							};
						}),
					};
				}),
			}));
		} catch (err) {
			toastError(err);
		}
	};

	return (
		<>
			<div className="min-w-[16rem]">
				<p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-default-500 dark:text-default-400">
					Ações
				</p>
				<div className="flex min-h-11 flex-wrap items-center gap-2 rounded-xl border border-default-200 bg-default-50 px-3 py-2 dark:border-default-700 dark:bg-default-900">
					{optionActions?.length ? (
						optionActions.map(action => (
							<span
								key={action.id}
								className={`inline-flex items-center gap-2 rounded-full border px-2.5 py-1 text-xs font-medium ${getActionTone(action.name)}`}
							>
								<span className="max-w-[12rem] truncate">
									{resolveActionLabel(action, lookups)}
								</span>
								<button
									type="button"
									className="rounded-full text-current/70 transition hover:text-current"
									onClick={() => handleDelete(action)}
									aria-label="Remover ação"
								>
									x
								</button>
							</span>
						))
					) : (
						<span className="text-xs text-default-500 dark:text-default-400">
							Nenhuma ação vinculada
						</span>
					)}

					<button
						type="button"
						className="inline-flex size-7 items-center justify-center rounded-full border border-dashed border-default-300 text-sm font-semibold text-default-500 transition hover:border-primary hover:text-primary dark:border-default-600 dark:text-default-400"
						onClick={() => setActionModalOpen(true)}
						aria-label="Adicionar ação"
					>
						+
					</button>
				</div>
			</div>

			<ActionModal
				data={{ users, queues, tags, connections }}
				open={actionModalOpen}
				handleClose={() => setActionModalOpen(false)}
				selectedChatbot={selectedChatbot}
				setSelectedChatbot={setSelectedChatbot}
				nodeId={nodeId}
				optionId={optionId}
			/>
		</>
	);
}
